'use client'
import React, { useRef, useImperativeHandle } from 'react'
import { Button, Heading, Picture, Text } from '../../components/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import type { ThreeDCarouselProps } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, EffectCoverflow } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-coverflow'

const componentType = 'carousel'
const componentName = 'three_d_carousel'

const ChevronLeft = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="three-d-carousel__nav-icon lg-desktop:scale-100 scale-[70%] text-white hover:text-[#1f1f1f]"
  >
    <circle cx="28" cy="28" r="28" fill="currentColor" fillOpacity="0.2" />
    <path d="M32 20L24 28L32 36" stroke="white" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ChevronRight = () => (
  <svg
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="three-d-carousel__nav-icon lg-desktop:scale-100 scale-[70%] text-white hover:text-[#1f1f1f]"
  >
    <circle cx="28" cy="28" r="28" fill="currentColor" fillOpacity="0.2" />
    <path d="M24 20L32 28L24 36" stroke="white" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ThreeDCarousel = React.forwardRef<HTMLDivElement, ThreeDCarouselProps>(({ data, className }, ref) => {
  const { title, items = [] } = data
  const cloneItems = items.length < 4 ? [...items, ...items] : items
  const swiperRef = useRef<SwiperType | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [stretchValue, setStretchValue] = React.useState(180)
  const { locale = 'us' } = useAiuiContext()

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  // 动态计算 stretch 值，让三个 slide 刚好占满容器宽度
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateStretch = () => {
      const containerWidth = container.offsetWidth
      // stretch 为容器宽度的 12.5%，让三个 slide (50% + 2*25%) 刚好占满
      const newStretch = Math.round(containerWidth * 0.125)
      setStretchValue(newStretch)

      // 更新 Swiper 配置
      if (swiperRef.current?.params?.coverflowEffect) {
        swiperRef.current.params.coverflowEffect.stretch = newStretch
        swiperRef.current.update()
      }
    }

    // 初始化计算
    updateStretch()

    // 监听容器尺寸变化
    const resizeObserver = new ResizeObserver(updateStretch)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  return (
    <section
      ref={boxRef}
      data-ui-component-id="ThreeDCarousel"
      className={cn('three-d-carousel laptop:overflow-hidden w-full overflow-visible', className)}
    >
      <Heading as="h1" size={4} html={title} className="three-d-carousel__title laptop:text-center text-left" />

      {/* Desktop carousel with 3D effect */}
      <div
        ref={containerRef}
        className="three-d-carousel__desktop laptop:block relative mx-auto mt-[24px] hidden w-full"
      >
        <Swiper
          onSwiper={swiper => (swiperRef.current = swiper)}
          centeredSlides={true}
          initialSlide={0}
          loop
          slidesPerView={'auto'}
          // loopAdditionalSlides={2}
          spaceBetween={0}
          grabCursor
          modules={[EffectCoverflow, Navigation]}
          slideToClickedSlide
          className="three-d-carousel__swiper rounded-box relative aspect-[1386/502] overflow-visible"
          effect="coverflow"
          coverflowEffect={{
            rotate: 0,
            stretch: stretchValue,
            depth: 300,
            modifier: 1,
            slideShadows: true,
          }}
        >
          {cloneItems.map((item, index) => (
            <SwiperSlide
              key={index}
              className="three-d-carousel__slide relative !w-1/2 cursor-grab overflow-hidden opacity-0 [.swiper-slide-active&]:opacity-100 [.swiper-slide-next&]:opacity-100 [.swiper-slide-prev&]:opacity-100"
            >
              {({ isActive }) => (
                <>
                  <Picture
                    source={item.imageUrl?.url || ''}
                    alt={item.imageUrl?.alt || item.title}
                    className={cn('three-d-carousel__image rounded-box mx-auto h-full overflow-hidden')}
                    imgClassName="h-full object-cover"
                    style={{
                      filter: isActive ? '' : 'brightness(50%) contrast(120%)',
                    }}
                  />
                  <div
                    className={cn(
                      'three-d-carousel__image-content tablet:p-[24px] desktop:p-[32px] text-info-primary absolute left-0 top-0 flex size-full flex-col justify-end gap-1 p-[16px]',
                      {
                        'aiui-dark': item.theme === 'dark',
                        'opacity-0': !isActive,
                      }
                    )}
                  >
                    <Heading as="h2" size={2} html={item.title} />
                    <Text
                      as="p"
                      size={4}
                      html={item.description}
                      className="three-d-carousel__image-description lg-desktop:text-[18px] desktop:text-[16px] text-[14px]"
                    />
                    {item.buttonText && (
                      <a
                        href={getLocalizedPath(item.buttonLink || '', locale)}
                        className="three-d-carousel__image-link "
                      >
                        <Button
                          size="base"
                          variant="secondary"
                          className="three-d-carousel__image-button desktop:mt-6 mt-4"
                        >
                          {item.buttonText}
                        </Button>
                      </a>
                    )}
                  </div>
                </>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="three-d-carousel__nav-controls laptop:px-[64px] desktop:px-[140px] lg-desktop:px-[200px] absolute left-1/2 top-1/2 z-20 flex w-full -translate-x-1/2 -translate-y-1/2 justify-between">
          <button
            className="three-d-carousel__nav-button three-d-carousel__nav-button--prev"
            onClick={() => swiperRef.current?.slidePrev()}
            aria-label="Previous slide"
          >
            <ChevronLeft />
          </button>
          <button
            className="three-d-carousel__nav-button three-d-carousel__nav-button--next"
            onClick={() => swiperRef.current?.slideNext()}
            aria-label="Next slide"
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      {/* Mobile carousel */}
      <div className="three-d-carousel__mobile laptop:hidden mt-[24px] block w-full overflow-visible">
        <Swiper
          loop={true}
          loopAdditionalSlides={1}
          slidesPerView={'auto'}
          spaceBetween={12}
          grabCursor
          className="three-d-carousel__swiper-mobile relative w-full !overflow-visible"
        >
          {cloneItems.map((item, index) => (
            <SwiperSlide
              key={index}
              className="three-d-carousel__slide-mobile relative !h-[360px] !w-[296px] cursor-grab overflow-hidden"
            >
              <Picture
                source={item.mobImageUrl?.url || item.imageUrl?.url || ''}
                alt={item.mobImageUrl?.alt || item.title}
                className="three-d-carousel__image-mobile rounded-box mx-auto h-full overflow-hidden"
                imgClassName="h-full object-cover"
              />
              <div
                className={cn(
                  'three-d-carousel__image-mobile-content tablet:p-[24px] desktop:p-[32px] text-info-primary absolute left-0 top-0 flex size-full flex-col justify-end gap-1 p-[16px]',
                  {
                    'aiui-dark': item.theme === 'dark',
                  }
                )}
              >
                <Heading as="h2" size={2} html={item.title} />
                <Text
                  as="p"
                  size={4}
                  html={item.description}
                  className="three-d-carousel__image-mobile-description text-[14px]"
                />
                {item.buttonText && (
                  <a
                    href={getLocalizedPath(item.buttonLink || '', locale)}
                    className="three-d-carousel__image-mobile-link "
                  >
                    <Button size="base" variant="secondary" className="three-d-carousel__image-mobile-button mt-3">
                      {item.buttonText}
                    </Button>
                  </a>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
})

ThreeDCarousel.displayName = 'ThreeDCarousel'

export default withLayout(ThreeDCarousel, { style: 'overflow: hidden;' })
